import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, BookOpen, ChevronRight, Download, Trash2 } from 'lucide-react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Footer from '../components/Footer';
import PublicAppHeader from '../components/PublicAppHeader';
import TrainingExerciseDetail from './TrainingExerciseDetail';
import {
  listOfflineTrainingExercises,
  removeOfflineTrainingExercise,
  type OfflineTrainingExercise,
} from '../lib/offlineTrainingStore';
import { resolvePortalShareDestination, resolveShareLinkApi } from '../lib/shareLinksApi';
import PublicTrainerNavigation from '../components/PublicTrainerNavigation';
import { useI18n } from '../i18n';

const PublicFrame: React.FC<{ children: React.ReactNode; libraryLayout?: boolean }> = ({ children, libraryLayout }) => (
  <div className="flex min-h-screen flex-col bg-gray-50">
    <PublicAppHeader />
    {libraryLayout && <PublicTrainerNavigation inlineOnWide />}
    <main className={libraryLayout
      ? 'mx-auto w-full max-w-[1440px] flex-1 px-4 py-5 pb-[calc(5rem+env(safe-area-inset-bottom))] sm:px-6 sm:pt-7 lg:pb-8'
      : 'mx-auto w-full max-w-7xl flex-1 px-0 py-0 pb-[calc(5rem+env(safe-area-inset-bottom))] sm:px-6 sm:py-8 min-[900px]:pb-8'}>
      {children}
    </main>
    <Footer />
    {!libraryLayout && <PublicTrainerNavigation />}
  </div>
);

export const PublicTrainingExerciseViewer: React.FC = () => {
  const location = useLocation();
  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const offlineId = search.get('offlineId') || undefined;
  const exerciseId = search.get('exerciseId') || undefined;
  return (
    <PublicFrame>
      <TrainingExerciseDetail
        exerciseIdentifier={offlineId || exerciseId}
        offlineOnly={Boolean(offlineId)}
        backTo={offlineId ? '/trainer/offline' : '/training-library/exercises'}
        flushMobileHeader
      />
    </PublicFrame>
  );
};

export const OfflineTrainingLibrary: React.FC = () => {
  const { t, locale } = useI18n();
  const [items, setItems] = useState<OfflineTrainingExercise[]>();
  const [failed, setFailed] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void listOfflineTrainingExercises().then(rows => {
      if (active) setItems(rows);
    }).catch(() => {
      if (active) setFailed(true);
    });
    return () => { active = false; };
  }, []);

  const remove = async (id: string) => {
    setRemoving(id);
    setFailed(false);
    try {
      await removeOfflineTrainingExercise(id);
      setItems(rows => rows?.filter(item => item.id !== id));
    } catch {
      setFailed(true);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <PublicFrame libraryLayout>
      <header className="mb-5 space-y-2">
        <h1 className="ui-page-title">{t('trainer_home_offline')}</h1>
        <p className="max-w-2xl text-sm leading-relaxed text-gray-600">{t('trainer_offline_description')}</p>
      </header>
      {failed && <p role="alert" className="mb-4 text-sm text-error-700">{t('trainer_offline_error')}</p>}
      {!items && !failed && <p role="status" className="text-sm text-gray-600">{t('trainer_home_loading')}</p>}
      {items?.length === 0 && (
        <section className="max-w-xl rounded-xl border border-gray-200 bg-white p-5">
          <span className="mb-3 grid h-10 w-10 place-items-center rounded-lg bg-orange-50 text-primary-700"><Download size={20} aria-hidden="true" /></span>
          <h2 className="ui-card-title">{t('trainer_offline_empty')}</h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">{t('trainer_offline_empty_hint')}</p>
          <Link to={`/${locale}/training-library/exercises`} className="mt-3 inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-primary-700 hover:underline">
            {t('trainer_find_exercise')} <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </section>
      )}
      {!!items?.length && <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {items.map(item => (
          <li key={item.id} className="flex min-w-0 items-center rounded-xl border border-gray-200 bg-white">
            <Link to={`/${locale}/trainer/viewer?offlineId=${encodeURIComponent(item.id)}`}
              className="group flex min-h-16 min-w-0 flex-1 items-center gap-3 rounded-l-xl px-3 py-2.5 hover:bg-gray-50">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-emerald-50 text-emerald-800"><BookOpen size={17} aria-hidden="true" /></span>
              <span className="min-w-0 flex-1">
                <span className="block break-words text-sm font-semibold text-gray-950">{item.title}</span>
                <span className="mt-0.5 block text-xs text-gray-600">{t('trainer_offline_saved_at', { date: new Date(item.savedAt).toLocaleDateString(locale) })}</span>
              </span>
              <ChevronRight size={17} className="shrink-0 text-gray-400" aria-hidden="true" />
            </Link>
            <button type="button" disabled={removing !== null} onClick={() => void remove(item.id)}
              aria-label={t('trainer_offline_remove', { title: item.title })} title={t('trainer_offline_remove', { title: item.title })}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-red-50 hover:text-error-700 disabled:opacity-50">
              <Trash2 size={17} aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>}
    </PublicFrame>
  );
};

export const TrainingShareResolver: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      if (!key) { setError('Ongeldige deellink.'); return; }
      try {
        const share = await resolveShareLinkApi(key);
        const destination = share ? resolvePortalShareDestination(share, key) : null;
        if (cancelled) return;
        if (!destination) { setError('Deze deellink is niet gevonden, verlopen of niet ondersteund.'); return; }
        navigate(destination, { replace: true, state: { shareAccessMode: share?.accessMode } });
      } catch {
        if (!cancelled) setError('De deellinkservice is momenteel niet beschikbaar.');
      }
    };
    void resolve();
    return () => { cancelled = true; };
  }, [key, navigate]);

  return (
    <PublicFrame>
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="max-w-md border-y border-gray-200 bg-white px-4 py-8 text-center">
          <p className="text-sm text-gray-600">{error || 'Deellink openen…'}</p>
          {error && <button type="button" onClick={() => navigate('/login', { replace: true })} className="mt-5 inline-flex min-h-11 items-center justify-center bg-gray-950 px-4 text-sm font-bold text-white">Naar inloggen</button>}
        </div>
      </div>
    </PublicFrame>
  );
};
